// src/main.js
import './assets/main.css'
import './index.css'
import '@/utils/kettle-polyfill'
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import 'flowbite'
import LoadingSpinner from './components/shared/Spinner.vue'
import 'element-plus/es/components/notification/style/css' // Import Notification styles
import MomentumScroll from './directives/momentumscroll'
import { useAppStore } from '@/stores/app'
import { useDataStore } from '@/stores/data'
import { trackException } from './services/analytics'
import { log } from '@/utils/logger'

window.setLoad = function (source, step, error) {
	if (!window.loadCount) window.loadCount = 0
	window.loadSource = source
	window.loadCount++
	window.loadStep = step
	window.loadError = error || null
	log('LOAD', source, step, window.loadCount).purple()
	if (step == 10) {
		//throw new Error(`Loading failed at step ${step}`)
	}
}

const isLocalDevHost = () => typeof location !== 'undefined' && (location.hostname === '127.0.0.1' || location.hostname === 'localhost')

const initializeVueApp = async () => {
	// Wait for kettle to be ready before mounting Vue app
	console.log('Waiting for kettle to be ready...')
	setLoad('main.js:initializeVueApp', 1)
	await window.waitForKettle()
	setLoad('main.js:initializeVueApp', 2)
	console.log('Kettle ready, initializing Vue app')

	const vueApp = createApp(App)
	vueApp.use(createPinia())
	vueApp.directive('momentum-scroll', MomentumScroll)
	vueApp.component('LoadingSpinner', LoadingSpinner)

	const shouldCaptureExceptions = !isLocalDevHost()
	const reportException = async (err, meta) => {
		if (!shouldCaptureExceptions) return
		try {
			// Delay exception logging to make sure all context is available
			setTimeout(async () => {
				const appStore = useAppStore()
				const dataStore = useDataStore()
				trackException(err, meta, appStore, dataStore)
				if (typeof kettle != 'undefined') {
					const debugMode = await kettle.get('debugMode')
					if (debugMode == '1') appStore.toast({ title: 'Ops.. noe gikk galt 😞', message: err.stack, type: 'error', duration: 30000 })
				}
			}, 2000)
		} catch (e) {
			console.error('reportException failed', e)
		}
	}

	if (shouldCaptureExceptions) {
		document.querySelectorAll('script[src]').forEach((s) => {
			s.addEventListener(
				'error',
				() => {
					reportException(new Error('Script load/parse error'), {
						type: 'script_load_error',
						source: s.src,
						url: location.href,
						user_agent: navigator.userAgent,
					})
				},
				{ once: true },
			)
		})
	}

	// Global Vue error handler (component errors)
	if (shouldCaptureExceptions) {
		vueApp.config.errorHandler = (err, instance, info) => {
			reportException(err, {
				type: 'vue_error',
				component: instance?.$?.type?.name || instance?.$?.type || 'anonymous',
				vue_info: info,
				url: typeof location !== 'undefined' ? location.href : '',
				user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
				fatal: false,
				info: info,
				object: err.object || '',
			})
		}
	}

	// Global window error handler (runtime errors)
	if (shouldCaptureExceptions && typeof window !== 'undefined') {
		window.addEventListener('error', (event) => {
			// Resource load errors (script/img/link) don't have .error/message
			const isResourceError = event.target && event.target !== window && (event.target.src || event.target.href)
			console.error(event)
			// event.error may be undefined for some script errors (CORS). Use message/source details.
			const err = event.error instanceof Error ? event.error : new Error(event.message || 'Script error')
			reportException(err, {
				type: isResourceError ? 'resource_error' : 'window_error',
				source: event.filename || event.target?.src || event.target?.href || '',
				lineno: event.lineno,
				colno: event.colno,
				url: location.href,
				user_agent: navigator.userAgent,
				fatal: false,
			})
		})

		// Unhandled promise rejections
		window.addEventListener('unhandledrejection', (event) => {
			console.error(event)
			const reason = event.reason
			const err = reason instanceof Error ? reason : new Error(typeof reason === 'string' ? reason : JSON.stringify(reason))
			reportException(err, {
				type: 'unhandled_rejection',
				url: location.href,
				user_agent: navigator.userAgent,
				fatal: false,
			})
		})
	}

	const vueAppInstance = vueApp.mount('#app')
	window.app = vueAppInstance
	setLoad('main.js:vueAppInstance', 5)

	console.log('Vue app mounted successfully with kettle ready')
}

// Start the initialization process
initializeVueApp().catch((error) => {
	console.error('Failed to initialize Vue app:', error)
	// Fallback: mount app anyway to prevent complete failure
	const vueApp = createApp(App)
	vueApp.use(createPinia())
	vueApp.mount('#app')
})
